import { MessageFormat } from 'messageformat';
import { catalog } from './catalog.ts';
import type { Locale, MessagePart, MessageSource, ResolveOptions } from './types.ts';

const DEFAULT_LOCALE: Locale = 'en-US';

// The catalog is the source of truth for which locales ship, so membership
// there is what makes an arbitrary string a Locale.
function isShippedLocale(value: string): value is Locale {
  return Object.hasOwn(catalog, value);
}

const SHIPPED_LOCALES: readonly Locale[] = Object.keys(catalog).filter(isShippedLocale);

// MF2's default bidi isolation wraps every placeholder in U+2068/U+2069
// directional-isolate marks. Useful when shipping RTL locales, but for
// an en-US-only catalog those characters surface as ugly noise in email
// subjects, test snapshots, and log lines. Revisit when an RTL locale
// lands and we have rendering tests to validate against.
const MF_OPTIONS = { bidiIsolation: 'none' } as const;

// Map a requested BCP 47 tag (typically navigator.language or an
// Accept-Language pick) to a shipped locale. Tries exact match, then
// language-prefix match (e.g., 'en-CA' against 'en-US'), then falls
// back to the canonical default.
function selectLocale(requested: string): Locale {
  if (isShippedLocale(requested)) {
    return requested;
  }
  const lang = requested.split('-', 1)[0];
  for (const shipped of SHIPPED_LOCALES) {
    if (shipped === lang || shipped.startsWith(`${lang}-`)) {
      return shipped;
    }
  }
  return DEFAULT_LOCALE;
}

// Parsing an MF2 source is expensive enough relative to formatting that
// we cache MessageFormat instances per (locale, source). Sources are
// drawn from the catalog so the keyspace is bounded by catalog size ×
// shipped locales — no unbounded growth.
const cache = new Map<string, MessageFormat>();

function getFormatter(locale: Locale, source: MessageSource): MessageFormat {
  const cacheKey = `${locale} ${source}`;
  const hit = cache.get(cacheKey);
  if (hit) return hit;
  const mf = new MessageFormat(locale, source, MF_OPTIONS);
  cache.set(cacheKey, mf);
  return mf;
}

export function resolve(key: string, options: ResolveOptions): string {
  const locale = selectLocale(options.locale);
  const { params, default: fallback } = options;
  const source = catalog[locale]?.[key];
  if (source === undefined) {
    if (fallback !== undefined) return fallback;
    throw new Error(
      `Content key not found: "${key}" (requested=${options.locale}, locale=${locale})`
    );
  }
  if (params === undefined && !needsFormatting(source)) {
    return source;
  }
  return getFormatter(locale, source).format(params, onFormatError);
}

// Resolve a message to its formatted parts instead of a flattened string, so
// callers can render inline MF2 markup (`{#name}…{/name}`) as real elements.
// Text runs — and any interpolated values — surface as `text` parts; markup
// placeholders surface as `markup` parts carrying the tag name and open/close
// kind. Callers that only need a string keep using resolve(), which drops the
// markup tags but preserves the text between them.
export function resolveToParts(key: string, options: ResolveOptions): MessagePart[] {
  const locale = selectLocale(options.locale);
  const { params, default: fallback } = options;
  const source = catalog[locale]?.[key];
  if (source === undefined) {
    if (fallback !== undefined) return [{ type: 'text', value: fallback }];
    throw new Error(
      `Content key not found: "${key}" (requested=${options.locale}, locale=${locale})`
    );
  }
  const formatted = getFormatter(locale, source).formatToParts(params, onFormatError);
  const parts: MessagePart[] = [];
  for (const part of formatted) {
    if (part.type === 'markup') {
      parts.push({ type: 'markup', kind: part.kind, name: part.name });
    } else if ('value' in part && part.value != null) {
      // text, and any interpolated number/string expression parts, fold to a
      // literal run. Zero-width parts (bidi isolation, valueless fallbacks) are
      // dropped — they carry no user-visible text.
      parts.push({ type: 'text', value: String(part.value) });
    }
  }
  return parts;
}

// Plain-text messages (no MF2 placeholders, no selector blocks) round-
// trip through MessageFormat unchanged, but parsing them still costs
// allocation. Skipping the formatter for these is a measurable win at
// catalog-wide read rates.
function needsFormatting(source: MessageSource): boolean {
  return source.includes('{') || source.startsWith('.');
}

function onFormatError(err: unknown): void {
  // MessageFormat.format swallows resolution errors and logs them as
  // warnings by default. Promote to a throw so missing/typo'd params
  // fail loudly in development and tests rather than rendering a
  // partial string. Production behavior follows the same rule —
  // failing visibly beats serving subtly-wrong text.
  throw err;
}
